/**
 * Quote 页客户端状态:
 *  - sessionId: 当前对话的 server 端 session_id (报价完成或新会话开始时重置)
 *  - turns: 本地缓存的聊天历史 (含乐观更新的 user 气泡 + 服务端权威 turns)
 *
 * 服务端是历史的最终权威: 每次 smart-quote 响应都会带 session_turns,
 * 我们用它整体替换 turns; 在等待响应期间用临时 seq=-1 显示用户气泡 (乐观更新)。
 */
import { create } from "zustand";
import type { SessionTurn } from "@/types/api";
import { quoteSnapshotFromStored, type QuoteSnapshot } from "./quoteFormat";

export interface LocalTurn extends Omit<SessionTurn, "seq" | "quote"> {
  seq: number; // -1 表示乐观气泡, 服务端响应后被替换
  /** 报价完成的那条 assistant 消息上挂报价明细, 归档后每一轮都保留卡片 */
  quote?: QuoteSnapshot;
}

interface QuoteState {
  sessionId: string;
  /** 已闭合轮次的消息 (报价完成后归档, 只在屏幕上保留展示) */
  archivedTurns: LocalTurn[];
  /** 当前进行中轮次的消息 (服务端 session_turns 只替换这一部分) */
  turns: LocalTurn[];
  pending: boolean;
  /** 用户输入触发: 立刻追加乐观 user 气泡 */
  beginSend: (text: string) => void;
  /** 服务端响应回来: 整体替换 turns (服务端是权威) */
  replaceTurns: (turns: SessionTurn[]) => void;
  /** 失败时: 移除乐观气泡 */
  rollbackOptimistic: () => void;
  finishPending: () => void;
  /** 把报价明细挂到当前轮最后一条 assistant 消息上 */
  attachQuote: (quote: QuoteSnapshot) => void;
  /** 报价完成: 当前轮消息归档 (屏幕保留), 换新会话 id 开启下一轮 */
  archiveRound: () => void;
  /** 「新对话」: 只在本地换新会话并清屏, 不删服务端记录 (问答记录里仍可查看) */
  closeSession: () => void;
  /** 用户继续追问: 沿用现有 sessionId, 没有则生成新的 */
  ensureSessionId: () => string;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export const useQuoteStore = create<QuoteState>((set, get) => ({
  sessionId: "",
  archivedTurns: [],
  turns: [],
  pending: false,

  beginSend: (text) => {
    set((s) => ({
      pending: true,
      turns: [
        ...s.turns,
        { seq: -1, role: "user", content: text, ts: new Date().toISOString() },
      ],
    }));
  },

  replaceTurns: (turns) => {
    set((s) => {
      // 报价卡片: 优先用服务端留存的快照 (quote_json), 没有时按 seq 保留本地已挂的
      const quotes = new Map(s.turns.filter((t) => t.quote).map((t) => [t.seq, t.quote]));
      return {
        turns: turns.map((t) => {
          const quote = quoteSnapshotFromStored(t.quote) ?? quotes.get(t.seq);
          return {
            seq: t.seq,
            role: t.role,
            content: t.content,
            ts: t.ts,
            ...(quote ? { quote } : {}),
          };
        }),
      };
    });
  },

  rollbackOptimistic: () => {
    set((s) => ({ turns: s.turns.filter((t) => t.seq !== -1) }));
  },

  finishPending: () => set({ pending: false }),

  attachQuote: (quote) =>
    set((s) => {
      const turns = [...s.turns];
      for (let i = turns.length - 1; i >= 0; i--) {
        if (turns[i].role === "assistant" && turns[i].seq !== -1) {
          turns[i] = { ...turns[i], quote };
          return { turns };
        }
      }
      // 没有 assistant 消息 (服务端没回 turns 且无 quote_response): 补一条空气泡承载卡片
      return {
        turns: [
          ...turns,
          { seq: Date.now(), role: "assistant", content: "报价结果如下:", ts: new Date().toISOString(), quote },
        ],
      };
    }),

  archiveRound: () =>
    set((s) => ({
      sessionId: "",
      archivedTurns: [...s.archivedTurns, ...s.turns.filter((t) => t.seq !== -1)],
      turns: [],
    })),

  closeSession: () => set({ sessionId: "", archivedTurns: [], turns: [], pending: false }),

  ensureSessionId: () => {
    const cur = get().sessionId;
    if (cur) return cur;
    const id = uuid();
    set({ sessionId: id });
    return id;
  },
}));
